import { FC, useCallback } from 'react';
import useRedirect from 'src/hooks/useRedirect';
import ComposeMessageModal from './ComposeMessageModal';
import ComposeMessagePage from './ComposeMessagePage';
import { useComposeMessageLogic } from './useComposeMessageLogic';

interface ComposeMessageProps {
  // Modal-specific props
  visible?: boolean;
  closeModal?: () => void;

  // Standalone page props
  isStandalone?: boolean;
  onNavigateBack?: () => void;

  // Common props
  replyToMessage?: {
    messageId: string;
    subject: string;
    sender: string;
  };

  // Pre-filled data
  initialRecipient?: string;
  initialSubject?: string;
  initialMessage?: string;
}

const ComposeMessage: FC<ComposeMessageProps> = ({
  visible = false,
  closeModal,
  isStandalone = false,
  onNavigateBack,
  replyToMessage,
  initialRecipient = '',
  initialSubject = '',
  initialMessage = '',
}) => {
  const { redirect } = useRedirect();

  // Business logic hook
  const {
    formData,
    selectedRecipients,
    errorState,
    isFormValid,
    isMessageSent,
    isSending,
    statusMessage,
    showStatus,
    handleInputChange,
    handleRecipientChange,
    handleFileUpload,
    removeAttachment,
    handleSend,
    resetForm,
    clearError,
  } = useComposeMessageLogic({
    initialRecipient,
    initialSubject,
    initialMessage,
    replyToMessage,
    onSuccess: () => {
      if (isStandalone && onNavigateBack) {
        // Communicate with MVC app to update left navigation to Outbox (full-screen mode only)
        redirect('/Buyer/Message/Outbox', true, false);
        onNavigateBack();
      } else if (closeModal) {
        closeModal();
      }
    },
  });

  const handleModalClose = useCallback(() => {
    resetForm();
    clearError();
    closeModal?.();
  }, [resetForm, clearError, closeModal]);

  const handleCancel = useCallback(() => {
    resetForm();
    clearError();

    if (isStandalone && onNavigateBack) {
      onNavigateBack();
    } else if (closeModal) {
      closeModal();
    }
  }, [isStandalone, onNavigateBack, closeModal, clearError, resetForm]);

  // Shared form props
  const formProps = {
    formData,
    selectedRecipients,
    errorState,
    statusMessage,
    showStatus,
    replyToMessage,
    onInputChange: handleInputChange,
    onRecipientChange: handleRecipientChange,
    onFileUpload: handleFileUpload,
    onRemoveAttachment: removeAttachment,
    onClearError: clearError,
  };

  // Send button should be disabled if form is invalid OR message was already sent OR currently sending
  const isSendDisabled = !isFormValid || isMessageSent || isSending;

  // Render as standalone page
  if (isStandalone) {
    return (
      <ComposeMessagePage
        {...formProps}
        isFormValid={isFormValid}
        isSendDisabled={isSendDisabled}
        onSend={handleSend}
        onCancel={handleCancel}
      />
    );
  }

  // Render as modal
  return (
    <ComposeMessageModal
      {...formProps}
      visible={visible}
      closeModal={handleModalClose}
      isFormValid={isFormValid}
      isSendDisabled={isSendDisabled}
      onSend={handleSend}
      onCancel={handleCancel}
    />
  );
};

export default ComposeMessage;
